import { useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../store/rootReducer";

interface UserModuleInfo {
  hasProduction: boolean;
  hasGeology: boolean;
  has3dGeoModel: boolean;
  hasMontney3dGeo: boolean;
  availablePlays: string[];
  geoPlays: string[];
}

export enum ModuleType {
  Production = 0,
  Forecast = 1,
  Geology = 2,
  ThreeDGeoModel = 3
}

/**
 * Custom hook that retrieves user module information.
 * @returns An object containing information about the modules user has access to.
 */
export default function useUserModules(): UserModuleInfo {
  /**
   * Retrieves the user object from the application state.
   */
  const user = useSelector((state: RootState) => state.auth.user);

  /**
   * Parses the user's module information from JSON format.
   *
   * EVA-150: guarded. `?? "{}"` covers null and undefined but not an empty string, which throws —
   * and this hook now sits behind both geo create modals and, via useCanCreateGeoJob, both
   * org-settings job pages, so a malformed `modules` claim would white-screen far more surface
   * than it used to. An unreadable claim means "no modules", never a crash.
   */
  const modules: { [play: string]: number[] } = (() => {
    try {
      return JSON.parse(user?.modules || "{}");
    } catch {
      return {};
    }
  })();

  // Memoized on the claim: the job filter depends on it, and a fresh array per render
  // would rebuild its filtered list every render.
  const geoPlays = useMemo(
    () =>
      Object.keys(modules)
        .filter(
          (play) =>
            modules[play].includes(ModuleType.Geology) ||
            modules[play].includes(ModuleType.ThreeDGeoModel)
        )
        .sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.modules]
  );

  /**
   * Checks if the user has a specific module.
   * @param module - The module type to check.
   * @param modules - The user's module information.
   * @returns True if the user has the specified module; otherwise, false.
   */
  const hasModule = (
    module: ModuleType,
    modules: { [play: string]: number[] }
  ): boolean => {
    /**
     * Retrieves the list of plays from the user's module information.
     */
    const plays = Object.keys(modules);

    /**
     * Iterates over each play and checks if the module is present.
     */
    for (const play of plays) {
      if (modules[play].indexOf(module) >= 0) {
        return true;
      }
    }

    return false;
  };

  const hasMontney3dGeo = (modules: { [play: string]: number[] }): boolean => {
    if ("Montney" in modules) {
      if (modules["Montney"].indexOf(ModuleType.ThreeDGeoModel) > -1) {
        return true;
      }
    }
    return false;
  };

  /**
   * Constructs and returns the user module information.
   */
  return {
    /**
     * Indicates whether the user has the Production module.
     */
    hasProduction: hasModule(ModuleType.Production, modules),
    /**
     * EVA-150: indicates whether the user has the Geology module for any play. Geology gates the
     * tops/bench/zone data plus the XDA, spacing and parent-child columns; the 3D Geo Model module
     * (below) gates only the sampled-3D columns on top of that.
     */
    hasGeology: hasModule(ModuleType.Geology, modules),
    /**
     * List of available plays extracted from the user's modules.
     */
    availablePlays: Object.keys(modules).sort(),
    /**
     * Indicates whether the user has the 3D Geo Model module.
     */
    has3dGeoModel: hasModule(ModuleType.ThreeDGeoModel, modules),

    hasMontney3dGeo: hasMontney3dGeo(modules),
    /**
     * EVA-148: plays holding Geology or 3D Geo Model, the tiers that entitle EVA Research jobs.
     */
    geoPlays
  };
}
